import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class NetworkPermissionService {

  constructor() { }

  /**
   * Verifica se o navegador é Chrome versão 142+ (ou outro naavegador),
   * onde as restrições de Local Network Access foram introduzidas.
   */
  isBrowserWithLNARestrictions(): boolean {
    return this.isChrome142Plus();
  }

  isChrome142Plus() {
    let navigator: any = window.navigator;

    if (!navigator.userAgentData) return false;

    const brands = navigator.userAgentData.brands;
    const chrome = brands.find((b: any) => b.brand === "Google Chrome");

    if (!chrome) return false;

    const major = parseInt(chrome.version.split('.')[0]);
    return major >= 142;
}

  /**
   * Verifica o estado da permissão de acesso à rede local.
   * @returns Promise<string> - 'granted', 'denied' ou 'prompt'
   */
  async checkLocalNetworkAccessPermission(): Promise<string> {
    try {
      // Check if permissions API is available and supports local-network-access
      if ('permissions' in navigator && 'query' in navigator.permissions) {
        const result = await (navigator.permissions as any).query({ name: 'local-network-access' });
        console.log(`LNA permission state: ${result.state}`);
        return result.state;
      }
    } catch (error) {
      console.warn('Local Network Access permission check not supported:', error);
    }
    
    return 'granted'; // Assume granted if not supported
  }

  /**
   * Verifica se a URL é para localhost (localhost, 127.0.0.1 ou [::1]).
   */
  isLocalhost(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    } catch {
      return false;
    }
  }

  /**
   * Verifica se um erro pode ser causado por permissão de rede local negada.
   * @param url - URL da requisição
   * @param statusCode - Código de status HTTP (0 para erros de rede, 504 para timeout)
   * @returns Promise<boolean> - true se for um erro de LNA
   * 
   * Nota: Chrome com Service Workers pode retornar status 504 (Gateway Timeout) 
   * na primeira requisição e status 0 nas subsequentes quando há problema de LNA.
   */
  async isLocalNetworkAccessError(url: string, statusCode: number): Promise<boolean> {
    // Verifica se é erro de rede (status 0) ou timeout 504
    // Status 504 pode ocorrer na primeira requisição para localhost quando há problemas de LNA
    if (statusCode !== 0 && statusCode !== 504) {
      return false;
    }

    // Verifica se é requisição para localhost
    if (!this.isLocalhost(url)) {
      return false;
    }

    // Verifica se é Browser com restrições LNA
    if (!this.isBrowserWithLNARestrictions()) {
      return false;
    }

    // Verifica o estado da permissão
    const permissionState = await this.checkLocalNetworkAccessPermission();
    return permissionState === 'denied' || permissionState === 'prompt';
  }
}
